import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { InvoiceEnums } from 'src/app/enum/invoice-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { InvoiceDetailModel } from 'src/app/models/invoicing/invoice-detail.model';
import { InvoiceModel } from 'src/app/models/invoicing/invoice.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoiceDetailRegisterModel } from 'src/app/models/tables/invoicing/invoice-detail-register.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ChargeAccountDetailService } from 'src/app/services/others/charge-account-detail.service';
import { ChargeAccountService } from 'src/app/services/others/charge-account.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-charge-account-detail',
  templateUrl: './charge-account-detail.component.html',
  styleUrls: ['./charge-account-detail.component.scss'],
  standalone: false
})
export class ChargeAccountDetailComponent implements OnChanges {
  @Input() chargeAccountModel: InvoiceModel = new InvoiceModel();
  @Input() disabled: boolean = false;
  @Input('isFormView')
  set changeFormView(change: boolean) {
    if (change) this.createNewDetail();
  }
  @Output() updateChargeAccount = new EventEmitter<InvoiceModel>();

  ascendant: boolean = true;
  chargeAccountDetail: InvoiceDetailModel = new InvoiceDetailModel();
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<InvoiceDetailRegisterModel> = new PagedResponseModel<InvoiceDetailRegisterModel>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = true;
  formChargeAccount: FormGroup = new FormGroup({});
  isFormView: boolean = false;
  limit: number = 10;
  output$ = new Subject<any>();
  page: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  constructor(
    private chargeAccountService: ChargeAccountService,
    private chargeAccountDetailService: ChargeAccountDetailService,
    private confirmDialogService: ConfirmDialogService,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit(): Promise<void> {
    await this.getColumnsForDetailsChargeAccount();
    this.tableActions = this.getDefaultActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    const chargeAccountModel = changes["chargeAccountModel"];

    if (chargeAccountModel && chargeAccountModel.currentValue.invoiceId != 0) {
      this.clearSelectionTrigger++;
      this.getDetailsChargeAccountWithPagination();
      this.chargeAccountDetail = new InvoiceDetailModel();
      this.disabledButton = this.chargeAccountModel.invoiceStateId != InvoiceEnums.abierta;
      this.tableActions = this.getDefaultActions();
    }
  }

  clearRegister() {
    this.chargeAccountDetail = new InvoiceDetailModel();
  }

  async createNewDetail() {
    this.chargeAccountDetail = new InvoiceDetailModel();
    this.getFormView(true);
  }

  async deleteInvoiceDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const accepted = await this.confirmDialogService.confirm("¿Realmente desea eliminar el detalle la cuenta de cobro?");
    if (accepted) {
      try {
        await firstValueFrom(this.chargeAccountDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle de la cuenta de cobro fue eliminado exitosamente.');
        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error al eliminar el detalle de cuenta de cobro.', error);
      }
    }
  }

  getChargeAccount(chargeAccount: InvoiceModel) {
    this.updateChargeAccount.emit(chargeAccount);
  }

  getChargeAccountDetail(chargeAccountDetail: InvoiceDetailModel) {
    this.chargeAccountDetail = chargeAccountDetail;
  }

  async getColumnsForDetailsChargeAccount() {
    try {
      this.detailHeader = await firstValueFrom(this.chargeAccountDetailService.getColumnsForDetailsChargeAccount());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getDetailsChargeAccountWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.chargeAccountDetailService.getChargeAccountsDetailsByChargeAccountIdWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.chargeAccountModel.invoiceId));
    } catch (error) {
      console.error('Error al buscar los detalles de las cuentas de cobro.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.chargeAccountModel.invoicingCutoffId == null && this.chargeAccountModel.invoiceStateId == InvoiceEnums.abierta) {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    } else {
      return [];
    }
  }

  async getUpdateListDetail() {
    this.getDetailsChargeAccountWithPagination();
    this.getChargeAccount(await firstValueFrom(this.chargeAccountService.getChargeAccountById(this.chargeAccountModel.invoiceId)));
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getUpdateListDetailByDelete() {
    this.chargeAccountDetail = new InvoiceDetailModel();
    this.clearSelectionTrigger++;
    this.getUpdateListDetail();
  }

  async onAction(event: { action: string, item?: InvoiceDetailRegisterModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      const response = await firstValueFrom(this.chargeAccountDetailService.getChargeAccountDetailById(event.item.invoiceDetailId));
      if (response) {
        this.chargeAccountDetail = response;
        this.isFormView = true;
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteInvoiceDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getDetailsChargeAccountWithPagination();
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
